import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, Music, Download, Star, Calendar } from 'lucide-react';
import { formatNumber } from '@/utils/format';

interface SongGridProps {
  songs: Array<{
    id: string;
    title: string;
    artist: string | null;
    album: string | null;
    slug: string;
    album_cover_url: string | null;
    sequence_count: number;
    total_downloads: number;
    average_rating: number;
    spotify_preview_url: string | null;
  }>;
}

export default function SongGrid({ songs }: SongGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {songs.map((song) => (
        <Link key={song.id} to={`/songs/${song.slug}`}>
          <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
            <CardHeader className="p-0">
              <div className="relative aspect-square overflow-hidden rounded-t-lg bg-muted">
                {song.album_cover_url ? (
                  <img
                    src={song.album_cover_url}
                    alt={`${song.album || song.title} cover`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-linear-to-br from-muted to-muted/50">
                    <Music className="h-16 w-16 text-muted-foreground" />
                  </div>
                )}
                
                {/* Play button overlay */}
                {song.spotify_preview_url && (
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button size="lg" className="rounded-full h-12 w-12" onClick={(e) => e.preventDefault()}>
                      <Play className="h-5 w-5 ml-0.5" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="p-4 space-y-3">
              <div>
                <h3 className="font-semibold line-clamp-2 text-base leading-tight">
                  {song.title}
                </h3>
                {song.artist && (
                  <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                    by {song.artist}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Music className="h-3 w-3" />
                  <span>{song.sequence_count} seq</span>
                </div>
                
                <div className="flex items-center gap-1">
                  <Download className="h-3 w-3" />
                  <span>{formatNumber(song.total_downloads)}</span>
                </div>

                {song.average_rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current text-yellow-500" />
                    <span>{song.average_rating.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Album info */}
              {song.album && (
                <p className="text-xs text-muted-foreground line-clamp-1">
                  {song.album}
                </p>
              )}
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}